import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCurrency } from '@/App';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon, TrendingUp, TrendingDown, Download, FileSpreadsheet, FileText, Filter, Package } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

const categoryLabels = {
  perfume: 'Perfume',
  cream: 'Crema',
  makeup: 'Maquillaje',
  other: 'Otro'
};

const COLORS = ['#BFA774', '#8FA58A', '#E6C9BE', '#262626'];

export default function ReportsPage() {
  const [salesReport, setSalesReport] = useState(null);
  const [projections, setProjections] = useState(null);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [users, setUsers] = useState([]);
  const [selectedSalesperson, setSelectedSalesperson] = useState("all");
  const [selectedChannel, setSelectedChannel] = useState("all");
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const { formatPrice, formatPYG } = useCurrency();

  useEffect(() => {
    fetchReports();
    fetchProjections();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/`);
      setUsers(response.data);
    } catch (error) {
      console.log("No se pudo cargar la lista de usuarios (posiblemente falta de permisos).");
    }
  };

  const fetchReports = async (startDate = null, endDate = null) => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedSalesperson && selectedSalesperson !== "all") params.salesperson_id = selectedSalesperson;
      if (selectedChannel && selectedChannel !== "all") params.sales_channel = selectedChannel;
      if (showOverdueOnly) params.overdue_only = true;

      const response = await axios.get(`${API}/reports/sales`, { params });
      setSalesReport(response.data);
    } catch (error) {
      toast.error('Error al cargar reporte de ventas');
    }
  };

  const fetchProjections = async () => {
    try {
      const response = await axios.get(`${API}/reports/projections`);
      setProjections(response.data);
    } catch (error) {
      toast.error('Error al cargar proyecciones');
    }
  };

  const handleDateRangeSelect = (range) => {
    setDateRange(range);
    // Reload if range is valid or cleared
    if ((range?.from && range?.to) || (!range?.from && !range?.to)) {
      const fromIso = range?.from ? range.from.toISOString() : null;
      const toIso = range?.to ? range.to.toISOString() : null;
      // We need to pass current state values as args because state updates might not be immediate
      fetchReports(fromIso, toIso);
    }
  };

  const handleFilterChange = (salespersonId, overdue) => {
    setSelectedSalesperson(salespersonId);
    setShowOverdueOnly(overdue);

    const fromIso = dateRange?.from ? dateRange.from.toISOString() : null;
    const toIso = dateRange?.to ? dateRange.to.toISOString() : null;

    // Manually construct params for immediate fetch
    const params = {};
    if (fromIso) params.start_date = fromIso;
    if (toIso) params.end_date = toIso;
    if (salespersonId && salespersonId !== "all") params.salesperson_id = salespersonId;
    if (selectedChannel && selectedChannel !== "all") params.sales_channel = selectedChannel;
    if (overdue) params.overdue_only = true;

    axios.get(`${API}/reports/sales`, { params })
      .then(response => setSalesReport(response.data))
      .catch(() => toast.error('Error al actualizar reporte'));
  };

  const handleChannelChange = (channel) => {
    setSelectedChannel(channel);

    const fromIso = dateRange?.from ? dateRange.from.toISOString() : null;
    const toIso = dateRange?.to ? dateRange.to.toISOString() : null;

    const params = {};
    if (fromIso) params.start_date = fromIso;
    if (toIso) params.end_date = toIso;
    if (selectedSalesperson && selectedSalesperson !== "all") params.salesperson_id = selectedSalesperson;
    if (showOverdueOnly) params.overdue_only = true;
    if (channel && channel !== "all") params.sales_channel = channel;

    axios.get(`${API}/reports/sales`, { params })
      .then(response => setSalesReport(response.data))
      .catch(() => toast.error('Error al actualizar reporte'));
  };

  // --- FUNCIÓN GENÉRICA DE EXPORTACIÓN (AHORA INCLUYE PDF) ---
  const handleExport = async (formatType) => {
    if (!salesReport) return toast.error('Datos de reporte no disponibles para exportar.');

    const params = {};
    if (dateRange?.from) params.start_date = dateRange.from.toISOString();
    if (dateRange?.to) params.end_date = dateRange.to.toISOString();
    if (selectedSalesperson && selectedSalesperson !== "all") params.salesperson_id = selectedSalesperson;
    if (selectedChannel && selectedChannel !== "all") params.sales_channel = selectedChannel;
    if (showOverdueOnly) params.overdue_only = true;

    // La URL de la API ahora puede manejar 'csv', 'xlsx' y 'pdf'
    const url = `${API}/reports/exports/${formatType}`;

    try {
      const response = await axios.get(url, {
        params: params,
        responseType: 'blob',
      });

      let filename = `reporte_ventas_${new Date().toISOString().split('T')[0]}.${formatType}`;
      const disposition = response.headers['content-disposition'];

      // Intentar obtener el nombre del archivo del header (si el backend lo envía)
      if (disposition && disposition.indexOf('attachment') !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Proceso de descarga del Blob
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`Reporte exportado a ${formatType.toUpperCase()}`);
    } catch (error) {
      toast.error('Error al exportar el reporte. Asegúrate de que el endpoint del backend esté operativo.');
      console.error('Error de exportación:', error);
    }
  };

  // --- FUNCIONES DE EXPORTACIÓN SIMPLIFICADAS ---
  const exportToCSV = () => {
    handleExport('csv');
  };

  const exportToExcel = () => {
    handleExport('xlsx');
  };

  const exportToPDF = () => {
    handleExport('pdf'); // Ahora llama a la API con 'pdf'
  };

  const exportPurchases = async (formatType) => {
    const params = {};
    if (dateRange?.from) params.start_date = dateRange.from.toISOString();
    if (dateRange?.to) params.end_date = dateRange.to.toISOString();

    const url = `${API}/reports/exports/purchases/${formatType}`;

    try {
      const response = await axios.get(url, { params, responseType: 'blob' });
      const filename = `compras_${new Date().toISOString().split('T')[0]}.${formatType}`;
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`Reporte de Compras (${formatType}) descargado.`);
    } catch (error) {
      toast.error("Error al exportar compras.");
    }
  };

  const exportExpenses = async (formatType) => {
    // Para gastos fijos, exportamos todo si no hay rango, o filtramos si lo hay.
    // El usuario indicó que NO necesita rango obligatorio.
    const params = {};
    if (dateRange?.from) params.start_date = dateRange.from.toISOString();
    if (dateRange?.to) params.end_date = dateRange.to.toISOString();

    const url = `${API}/reports/exports/expenses/${formatType}`;

    try {
      const response = await axios.get(url, { params, responseType: 'blob' });
      const filename = `gastos_${new Date().toISOString().split('T')[0]}.${formatType}`;
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`Reporte de Gastos (${formatType}) descargado.`);
    } catch (error) {
      toast.error("Error al exportar gastos.");
    }
  };

  const exportInventory = async () => {
    // Inventory export is a separate endpoint
    try {
      const response = await axios.get(`${API}/inventory/export-excel`, {
        responseType: 'blob',
      });

      const current = new Date().toISOString().split('T')[0];
      const filename = `inventario_detallado_${current}.xlsx`;

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("Inventario exportado exitosamente");
    } catch (error) {
      toast.error("Error al exportar inventario");
      console.error(error);
    }
  };
  // ---------------------------------------------

  const dailySalesData = salesReport?.daily_breakdown
    ? Object.entries(salesReport.daily_breakdown).map(([date, amount]) => ({
      date: new Date(date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
      Ingresos: amount.revenue
    }))
    : [];

  const categorySalesData = salesReport?.category_sales
    ? Object.entries(salesReport.category_sales).map(([category, amount]) => ({
      name: categoryLabels[category] || category.charAt(0).toUpperCase() + category.slice(1),
      Ventas: amount
    }))
    : [];

  const channelSalesData = salesReport?.channel_sales
    ? Object.entries(salesReport.channel_sales).map(([channel, amount]) => ({
      name: channel === 'online' ? 'Tienda Online' : (channel === 'local' ? 'Local' : channel),
      Ventas: amount
    }))
    : [];

  const monthlySalesData = projections?.monthly_sales
    ? Object.entries(projections.monthly_sales).map(([month, amount]) => ({
      month: new Date(month).toLocaleDateString('es-ES', { month: 'short', year: 'numeric' }),
      Ventas: amount, // Rename key for tooltip consistency
      amount: amount // Keep amount for potentially other usages if any
    }))
    : [];


  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Aseguramos que el tooltip siempre muestre la etiqueta del dato (Ingresos o Ventas)
      const dataEntry = payload[0];

      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg text-sm">
          <p className="font-semibold text-gray-900 mb-1">{label}</p>
          <div className="flex items-center">
            {/* Color del indicador basado en el color del trazo/barra */}
            <div
              className="w-2 h-2 rounded-full mr-2"
              style={{ backgroundColor: dataEntry.color }}
            ></div>
            <p className="text-gray-600">
              {/* dataEntry.name será "Ingresos" o "Ventas" */}
              {dataEntry.name}: <span className="font-bold text-gray-900">{formatPYG(dataEntry.value)}</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!salesReport || !projections) {
    return <div className="text-center py-12">Cargando reportes...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-bold" data-font="display">Reportes y Análisis</h1>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="reports-date-range">
                <CalendarIcon className="mr-2 size-4" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    `${format(dateRange.from, 'PP', { locale: es })} - ${format(dateRange.to, 'PP', { locale: es })}`
                  ) : (
                    format(dateRange.from, 'PP', { locale: es })
                  )
                ) : (
                  'Seleccionar rango de fechas'
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={handleDateRangeSelect}
                numberOfMonths={2}
                locale={es}
              />
            </PopoverContent>
          </Popover>



          {/* Salesperson Filter */}
          {users.length > 0 && (
            <Select
              value={selectedSalesperson}
              onValueChange={(val) => handleFilterChange(val, showOverdueOnly)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Vendedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los vendedores</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Channel Filter */}
          <Select
            value={selectedChannel}
            onValueChange={handleChannelChange}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Canal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los canales</SelectItem>
              <SelectItem value="local">Local</SelectItem>
              <SelectItem value="online">Tienda Online</SelectItem>
            </SelectContent>
          </Select>

          {/* Overdue Checkbox */}
          <div className="flex items-center space-x-2 bg-background border px-3 py-2 rounded-md">
            <Checkbox
              id="overdue"
              checked={showOverdueOnly}
              onCheckedChange={(checked) => handleFilterChange(selectedSalesperson, checked)}
            />
            <Label htmlFor="overdue" className="text-sm font-medium cursor-pointer">
              Solo Vencidos
            </Label>
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline">
                <Download className="mr-2 size-4" />
                Exportar
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56" align="end">
              <div className="space-y-1 p-2">
                <p className="text-xs font-semibold text-muted-foreground mb-1 px-2">Ventas</p>
                <Button variant="ghost" className="w-full justify-start h-8 px-2" onClick={exportToExcel}>
                  <FileSpreadsheet className="mr-2 size-4" /> Excel
                </Button>
                <Button variant="ghost" className="w-full justify-start h-8 px-2" onClick={exportToCSV}>
                  <FileSpreadsheet className="mr-2 size-4" /> CSV
                </Button>
                <Button variant="ghost" className="w-full justify-start h-8 px-2" onClick={exportToPDF}>
                  <FileText className="mr-2 size-4" /> PDF
                </Button>

                <div className="my-1 border-t" />
                <p className="text-xs font-semibold text-muted-foreground mb-1 px-2">Compras</p>
                <Button variant="ghost" className="w-full justify-start h-8 px-2" onClick={() => exportPurchases('xlsx')}>
                  <FileSpreadsheet className="mr-2 size-4" /> Excel
                </Button>
                <Button variant="ghost" className="w-full justify-start h-8 px-2" onClick={() => exportPurchases('csv')}>
                  <FileSpreadsheet className="mr-2 size-4" /> CSV
                </Button>

                <div className="my-1 border-t" />
                <p className="text-xs font-semibold text-muted-foreground mb-1 px-2">Gastos</p>
                <Button variant="ghost" className="w-full justify-start h-8 px-2" onClick={() => exportExpenses('xlsx')}>
                  <FileSpreadsheet className="mr-2 size-4" /> Excel
                </Button>
                <Button variant="ghost" className="w-full justify-start h-8 px-2" onClick={() => exportExpenses('csv')}>
                  <FileSpreadsheet className="mr-2 size-4" /> CSV
                </Button>

                <div className="my-1 border-t" />
                <Button variant="ghost" className="w-full justify-start h-8 px-2" onClick={exportInventory}>
                  <Package className="mr-2 size-4" /> Inventario
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Ingresos Totales</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-font="display">{formatPYG(salesReport.summary['total_revenue'])}</div>
            <p className="text-xs text-muted-foreground mt-1">De {salesReport.summary['total_sales_count']} ventas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Ticket Promedio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-font="display">{formatPYG(salesReport.summary['avg_ticket'])}</div>
            <p className="text-xs text-muted-foreground mt-1">Por transacción</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Proyección Próximo Mes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-font="display">{formatPYG(projections.next_month_projection)}</div>
            <div className="flex items-center gap-1 mt-1">
              {projections.trend === 'up' ? (
                <><TrendingUp className="size-3 text-[#2E7D32]" /><span className="text-xs text-[#2E7D32]">Tendencia al alza</span></>
              ) : projections.trend === 'down' ? (
                <><TrendingDown className="size-3 text-destructive" /><span className="text-xs text-destructive">Tendencia a la baja</span></>
              ) : (
                <span className="text-xs text-muted-foreground">Estable</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cargos y Descuentos (tienda online) */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Delivery Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-font="display">{formatPYG(salesReport.summary['total_delivery_pyg'] || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Cobros de entrega (ventas online)</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">IVA Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-font="display">{formatPYG(salesReport.summary['total_iva_pyg'] || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">IVA recaudado (ventas online)</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Descuento por Cupones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-font="display">{formatPYG(salesReport.summary['total_coupon_discount_pyg'] || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Descuentos de cupones aplicados</p>
          </CardContent>
        </Card>
      </div>

      {/* Ventas por Canal */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle data-font="display">Ventas por Canal</CardTitle>
          </CardHeader>
          <CardContent>
            {channelSalesData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Sin datos por canal.</p>
            ) : (
              <div className="h-64">
                <BarChart
                  data={channelSalesData}
                  style={{ width: '100%', height: '100%' }}
                  responsive
                  margin={{ left: 10, right: 30, top: 8, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity="0.2" />
                  <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="Ventas" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle data-font="display">Tendencia de Ventas Diarias</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <AreaChart data={dailySalesData} responsive style={{ width: '100%', height: '100%' }} margin={{ left: 10, right: 30, top: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradPrimary" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity="0.2" />
                <XAxis dataKey="date" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <Tooltip
                  content={<CustomTooltip />}
                />
                <Area type="monotone" dataKey="Ingresos" stroke="hsl(var(--primary))" fill="url(#gradPrimary)" strokeWidth={2} />
              </AreaChart>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle data-font="display">Ventas por Categoría</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <BarChart
                data={categorySalesData}
                style={{ width: '100%', maxWidth: '700px', maxHeight: '70vh', aspectRatio: 1.618 }}
                responsive
                margin={{ left: 0, right: 0, top: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity="0.2" />
                <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <Tooltip
                  content={<CustomTooltip />}
                />
                <Bar dataKey="Ventas" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pie Chart & Monthly Sales */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle data-font="display">Distribución de Ventas por Categoría</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <PieChart responsive style={{ height: 'calc(100% - 20px)', width: '33%', flex: '1 1 200px', aspectRatio: 1 }}>
                <Pie
                  data={categorySalesData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="Ventas"
                >
                  {categorySalesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={<CustomTooltip />}
                />
                <Legend />
              </PieChart>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle data-font="display">Ventas Mensuales y Proyecciones (últimos 6 Meses)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <LineChart
                data={monthlySalesData}
                margin={{ left: 0, right: 0, top: 5, bottom: 5 }}
                style={{ width: '100%', maxWidth: '700px', height: '100%', maxHeight: '70vh', aspectRatio: 1.618 }}
                responsive
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity="0.2" />
                <XAxis dataKey="month" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <Tooltip
                  content={<CustomTooltip />}
                />
                <Line type="monotone" dataKey="amount" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </div>
          </CardContent>
        </Card>
      </div>
    </div >
  );
}
